// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./AssembliesConsoleEditAdd.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	deleteAPICall,
	getAPICall,
	postAPICall,
	putAPICall,
} from "../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";

// REACT COMPONENT
const AssembliesConsoleEditAdd = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [loading, setLoading] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	const [assemblyName, setAssemblyName] = useState("");

	// Assembly type
	const [allAssemblyTypes, setAllAssemblyTypes] = useState([]);
	const [selectedAssemblyType, setSelectedAssemblyType] = useState(null);

	// Edit-mode association states
	const [allDetails, setAllDetails] = useState([]);
	const [allGroupings, setAllGroupings] = useState([]);
	const [selectedDetails, setSelectedDetails] = useState([]);
	const [selectedGroupings, setSelectedGroupings] = useState([]);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		if (!props.open) return;
		if (props.editing && props.recordToEdit) {
			setAssemblyName(props.recordToEdit.name);
			fetchEditData();
		} else if (!props.editing) {
			fetchAssemblyTypes();
		}
	}, [props.open, props.recordToEdit]);

	// API FUNCTIONS
	const fetchAssemblyTypes = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/encompass/assemblyTypes/all"
			);
			setAllAssemblyTypes(res.data || []);
		} catch (err) {
			console.error("Error fetching assembly types:", err);
		}
	};

	const fetchEditData = async () => {
		setLoading(true);
		try {
			const [
				assemblyTypesRes,
				allDetailsRes,
				allGroupingsRes,
				selDetailsRes,
				selGroupingsRes,
			] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/encompass/assemblyTypes/all"),
				getAPICall(instance, accounts[0], "/api/encompass/details/all"),
				getAPICall(instance, accounts[0], "/api/encompass/groupings/all"),
				getAPICall(
					instance,
					accounts[0],
					"/api/encompass/assemblies/detailsByAssembly",
					{ assemblyID: props.recordToEdit.id }
				),
				getAPICall(
					instance,
					accounts[0],
					"/api/encompass/groupings/byAssembly",
					{ assemblyIDs: props.recordToEdit.id }
				),
			]);
			const types = assemblyTypesRes.data || [];
			setAllAssemblyTypes(types);
			setSelectedAssemblyType(
				types.find((t) => t.id === props.recordToEdit.assembly_type_id) ?? null
			);
			setAllDetails(allDetailsRes.data);
			setAllGroupings(allGroupingsRes.data);
			setSelectedDetails(selDetailsRes.data);
			setSelectedGroupings(selGroupingsRes.data);
		} catch (err) {
			console.error("Error fetching edit data:", err);
		}
		setLoading(false);
	};

	// INPUT HANDLERS
	const assemblyNameChanged = (event) => {
		setAssemblyName(event.target.value);
	};

	// API FUNCTIONS
	const handleUpdateRecord = async () => {
		await putAPICall(instance, accounts[0], "/api/encompass/assemblies/update", {
			id: props.recordToEdit.id,
			name: assemblyName,
			assemblyTypeID: selectedAssemblyType?.id || null,
			modifiedBy: employeeID,
		});

		// Sync detail associations
		await deleteAPICall(
			instance,
			accounts[0],
			"/api/encompass/assemblies/deleteByAssembly",
			{ assemblyID: props.recordToEdit.id }
		);
		for (const detail of selectedDetails) {
			await postAPICall(
				instance,
				accounts[0],
				"/api/encompass/assemblies/add",
				{
					detailID: detail.id,
					assemblyID: props.recordToEdit.id,
					modifiedBy: employeeID,
				}
			);
		}

		// Sync grouping associations
		await deleteAPICall(
			instance,
			accounts[0],
			"/api/encompass/assemblies/deleteGroupingsByAssembly",
			{ assemblyID: props.recordToEdit.id }
		);
		for (const grouping of selectedGroupings) {
			await postAPICall(
				instance,
				accounts[0],
				"/api/encompass/assemblies/addGrouping",
				{
					assemblyID: props.recordToEdit.id,
					groupingID: grouping.id,
					modifiedBy: employeeID,
				}
			);
		}
	};

	const handleAddRecord = async () => {
		await postAPICall(
			instance,
			accounts[0],
			"/api/encompass/assemblies/addAssembly",
			{
				name: assemblyName,
				assemblyTypeID: selectedAssemblyType?.id || null,
				modifiedBy: employeeID,
			}
		);
	};

	const handleDeleteRecord = async () => {
		await deleteAPICall(
			instance,
			accounts[0],
			"/api/encompass/assemblies/deleteAssembly",
			{ id: props.recordToEdit.id }
		);
	};

	// HELPER FUNCTIONS
	const handleSubmitClick = async () => {
		if (!validateInputFields()) return;
		setLoading(true);
		try {
			if (props.editing) {
				await handleUpdateRecord();
			} else {
				await handleAddRecord();
			}
		} catch (err) {
			console.error(err);
		}
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose(true, !props.editing ? assemblyName : null);
	};

	const handleCancelClick = () => {
		setAlertMessage("");
		setAlertOpen(false);
		props.onClose(false);
	};

	const handleDeleteClick = async () => {
		setLoading(true);
		await handleDeleteRecord();
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose(true);
	};

	const resetInputFields = () => {
		setAssemblyName("");
		setAllAssemblyTypes([]);
		setSelectedAssemblyType(null);
		setAllDetails([]);
		setAllGroupings([]);
		setSelectedDetails([]);
		setSelectedGroupings([]);
	};

	const validateInputFields = () => {
		if (assemblyName === "") {
			setAlertMessage("Assembly name is required");
			setAlertOpen(true);
			return false;
		}
		if (!selectedAssemblyType) {
			setAlertMessage("Assembly type is required");
			setAlertOpen(true);
			return false;
		}
		return true;
	};

	// RENDER
	return (
		<Dialog
			id="AssembliesEditAddDialog"
			open={props.open}
			fullWidth
			maxWidth="md"
			onClose={handleCancelClick}
			TransitionProps={{ onExited: resetInputFields }}
		>
			<DialogTitle id="AssembliesEditAddDialogTitle">
				{props.editing ? "Edit Assembly" : "Add Assembly"}
				{props.editing && (
					<Button
						color="error"
						variant="contained"
						size="small"
						onClick={handleDeleteClick}
					>
						Delete
					</Button>
				)}
			</DialogTitle>

			<DialogContent id="AssembliesEditAddDialogContent">
				{loading ? (
					<div className="editAssembliesLoadingContainer">
						<CircularProgress />
					</div>
				) : (
					<>
						{alertOpen && (
							<Alert
								severity="warning"
								sx={{ mb: 2 }}
								action={
									<IconButton
										aria-label="close"
										color="inherit"
										size="small"
										onClick={() => setAlertOpen(false)}
									>
										<CloseIcon />
									</IconButton>
								}
							>
								{alertMessage}
							</Alert>
						)}

						<TextField
							id="assemblyName"
							label="Assembly Name"
							variant="outlined"
							value={assemblyName}
							onChange={assemblyNameChanged}
							fullWidth
						/>

						<Autocomplete
							sx={{ mt: 2 }}
							options={allAssemblyTypes}
							value={selectedAssemblyType}
							getOptionLabel={(option) => option.name ?? ""}
							isOptionEqualToValue={(option, value) =>
								option.id === value.id
							}
							onChange={(_, newValue) =>
								setSelectedAssemblyType(newValue)
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Assembly Type"
									placeholder="Select assembly type..."
								/>
							)}
						/>

						{props.editing && (
							<>
								<Autocomplete
										sx={{ mt: 2 }}
										multiple
										limitTags={4}
										options={allDetails}
										value={selectedDetails}
										getOptionLabel={(option) => option.title ?? ""}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										filterSelectedOptions
										onChange={(_, newValue) =>
											setSelectedDetails(newValue)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Associated Details"
												placeholder="Search details..."
											/>
										)}
									/>
									<Autocomplete
										sx={{ mt: 2 }}
										multiple
										limitTags={4}
										options={allGroupings}
										value={selectedGroupings}
										getOptionLabel={(option) => option.name ?? ""}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										filterSelectedOptions
										onChange={(_, newValue) =>
											setSelectedGroupings(newValue)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Associated Groupings"
												placeholder="Search groupings..."
											/>
										)}
									/>
							</>
						)}

						<div className="editAssembliesButtons" style={{ marginTop: 16 }}>
							<Button
								variant="outlined"
								color="error"
								onClick={handleCancelClick}
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmitClick}
							>
								Submit
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

// EXPORT
export default AssembliesConsoleEditAdd;
